import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameCard from '@components/GameCard';
import {
  getCategoryBySlug,
  getGamesByCategory,
  getFeaturedGames,
  getTrendingGames,
  getLatestGames,
} from '@lib/content';
import {
  buildCategorySchema,
  buildCategoryBreadcrumbSchema,
  combineSchemas,
} from '@lib/jsonld';
import { getPrimaryCategories } from '@lib/game-utils';

const ITEMS_PER_LOAD = 12;
const CategoryPage: React.FC = () => {
  const { slug } = useParams();

  // Handle special categories and hardcoded categories
  const category = useMemo(() => {
    if (!slug) return null;

    // Special categories
    if (slug === 'featured') {
      return { slug: 'featured', name: 'Featured', description: 'Our hand-picked selection of the best games' };
    } else if (slug === 'trending') {
      return { slug: 'trending', name: 'Trending', description: 'The most popular games right now' };
    } else if (slug === 'latest') {
      return { slug: 'latest', name: 'Latest', description: 'Newest games added to our collection' };
    }

    // Try to get from content system first
    const contentCategory = getCategoryBySlug(slug);
    if (contentCategory) return contentCategory;

    // Fallback to hardcoded categories
    const hardcodedCategories = [
      { slug: 'racing', name: 'Racing', description: 'Fast-paced racing games and speed challenges' },
      { slug: 'driving', name: 'Driving', description: 'Driving simulation and car control games' },
      { slug: 'car', name: 'Car', description: 'All kinds of car-related games and automotive fun' },
      { slug: 'drift', name: 'Drift', description: 'Master the art of drifting and sliding' },
      { slug: 'simulation', name: 'Simulation', description: 'Realistic simulation games and experiences' },
      { slug: '3d', name: '3D', description: 'Immersive 3D games with stunning graphics' },
      { slug: 'skill', name: 'Skill', description: 'Test your skills and precision' },
      { slug: 'sports', name: 'Sports', description: 'Sports games and athletic challenges' }
    ];

    return hardcodedCategories.find(cat => cat.slug === slug) || null;
  }, [slug]);

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);

  const allGames = useMemo(() => {
    if (!slug) return [];

    if (slug === 'featured') {
      return getFeaturedGames();
    } else if (slug === 'trending') {
      return getTrendingGames();
    } else if (slug === 'latest') {
      return getLatestGames();
    } else {
      return getGamesByCategory(slug);
    }
  }, [slug]);

  const games = useMemo(() => {
    // Categories that support load more functionality
    const loadMoreCategories = ['latest', 'racing', 'driving', 'car', 'drift', 'simulation', '3d', 'skill', 'sports'];

    if (slug && loadMoreCategories.includes(slug)) {
      return allGames.slice(0, visibleCount);
    }
    return allGames;
  }, [slug, allGames, visibleCount]);

  const primaryCategories = useMemo(() => [
    { slug: 'racing', name: 'Racing' },
    { slug: 'driving', name: 'Driving' },
    { slug: 'car', name: 'Car' },
    { slug: 'drift', name: 'Drift' },
    { slug: 'simulation', name: 'Simulation' },
    { slug: '3d', name: '3D' },
    { slug: 'skill', name: 'Skill' },
    { slug: 'sports', name: 'Sports' }
  ], []);

  const jsonLdSchemas = useMemo(() => {
    if (!category || !games.length) return undefined;

    const categorySchema = buildCategorySchema(category, games);
    const breadcrumbSchema = buildCategoryBreadcrumbSchema(category);

    return combineSchemas(categorySchema, breadcrumbSchema);
  }, [category, games]);

  if (!slug || !category) {
    return <p className="muted">Category not found.</p>;
  }

  return (
    <>
      <SeoHead
        title={`${category.name} games`}
        description={category.description ?? `Play ${category.name} games online.`}
        jsonLd={jsonLdSchemas}
      />

      {/* ===== HEADER CATEGORY ===== */}
      <section className="masthead">
        <div className="container">
          <div className="row mb-2">
            <div className="text-center mx-auto">
              <h1 className="display-6 fw-bold">
                {category.name.toUpperCase()} GAMES
              </h1>
              <h2>{category.description}</h2>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BODY ===== */}
      <div className="page-body">
        <div className="container">

          {/* ===== CATEGORY TABS ===== */}
          <div className="category-pills">
            {primaryCategories.map((cat) => (
              <Link
                key={cat.slug}
                className={`category-pill ${cat.slug === category.slug ? 'active' : ''}`}
                to={`/category/${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* ===== GAME LIST ===== */}
          <section className="my-5 game-section" id="Featured">
            <h2 className="text-center section-title">
              {`${category.name.toUpperCase()} GAMES (${allGames.length})`}
            </h2>

            <div className="row">
              {games.map((game) => (
                <div
                  key={game.slug}
                  className="col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2 game-animate"
                >
                  <GameCard game={game} />
                </div>
              ))}
            </div>

            {/* LOAD MORE cho latest, racing, arcade, action, adventure, puzzle, strategy */}
            {slug && ['latest', 'racing', 'driving', 'car', 'drift', 'simulation', '3d', 'skill', 'sports'].includes(slug) && visibleCount < allGames.length && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-outline-primary px-4 py-2 load-more-btn"
                  onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_LOAD)}
                >
                  Load More
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
